import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import Typography from 'material-ui/Typography';
import Button from 'material-ui/Button';
import { FormControl } from 'material-ui/Form';
import Input, { InputLabel } from 'material-ui/Input';
import Grid from 'material-ui/Grid';
import BirthDateFormatCustom from '../../common/BirthDateFormatCustom';
import ContinueButton from '../../common/components/Buttons/ContinueButton';
import colors from '../../../config/themeColors';

const styles = ({ spacing: { unit } }) => ({
    root: {
        width: 'calc(100% - 32px)',
        marginLeft: '16px',
        marginRight: '16px',
    },
    button: {
        marginLeft: unit * 2,
        '&:first-child': {
            marginLeft: 0,
        },
    },
    buttons: {
        marginTop: unit * 2,
        marginBottom: unit * 2,
    },
    formDesc: {
        marginTop: '16px',
        color: colors.brownishGrey,
    },
    formControl: {
        marginTop: unit * 3,
        minWidth: unit * 15,
        width: '100%',
        '&:not(:last-of-type)': {
            marginRight: unit * 3,
        },
    },
});

function ApplicantDetails(props) {
    const isValidDate = (date) => {
        const matches = /^(\d{2})[/](\d{2})[/](\d{4})$/.exec(date);
        if (matches == null) return false;
        const m = matches[2] - 1;
        const d = matches[1];
        const y = matches[3];
        const composedDate = new Date(y, m, d);
        // eslint-disable-next-line
        return composedDate.getDate() == d &&
            // eslint-disable-next-line
            composedDate.getMonth() == m &&
            // eslint-disable-next-line
            composedDate.getFullYear() == y &&
            composedDate.getFullYear() < new Date().getFullYear();
    };

    // https://stackoverflow.com/a/11082955/4385065
    const isValidPostalCode = (string) => {
        const regex1 = /^([A-Z]{1,2})([0-9]{2,3})([A-Z]{2})$/i;
        const regex2 = /^([A-Z]{1,2})([0-9])([A-Z])([0-9])([A-Z]{2})$/i;
        const trimmedString = string.replace(/\s/g, '');

        return string && (regex1.test(trimmedString) || regex2.test(trimmedString));
    };

    // https://stackoverflow.com/a/32744109/4385065
    const isValidPhone = (string) => {
        // eslint-disable-next-line
        const regex = /^\(?(?:(?:0(?:0|11)\)?[\s-]?\(?|\+)44\)?[\s-]?\(?(?:0\)?[\s-]?\(?)?|0)(?:\d{2}\)?[\s-]?\d{4}[\s-]?\d{4}|\d{3}\)?[\s-]?\d{3}[\s-]?\d{3,4}|\d{4}\)?[\s-]?(?:\d{5}|\d{3}[\s-]?\d{3})|\d{5}\)?[\s-]?\d{4,5}|8(?:00[\s-]?11[\s-]?11|45[\s-]?46[\s-]?4\d))(?:(?:[\s-]?(?:x|ext\.?\s?|\#)\d+)?)$/;
        return string && regex.test(string);
    };

    const isFormValid = () => (
        props.applicant.name &&
        props.applicant.surname &&
        isValidDate(props.applicant.dateOfBirth) &&
        props.applicant.address.city &&
        isValidPostalCode(props.applicant.address.postalCode) &&
        props.applicant.address.street &&
        props.applicant.address.houseNumber &&
        isValidPhone(props.applicant.phone)
    );

    const goToNextStep = () => {
        props.setStepCompleted(3, true);
        props.setStep(4);
        props.setApplicantDetails({
            ...props.applicant,
            customer: props.customer,
            applicationId: props.applicationId,
        });
    };
    const setStepUncompleted = () => {
        props.setStepCompleted(3, false);
        props.setStepCompleted(4, false);
    };
    return (
        <div className={props.classes.root}>
            <Typography className={props.classes.formDesc}>
                All fields are required.
            </Typography>
            <div>
                <Grid
                    container
                    spacing={16}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                    >
                        <FormControl
                            className={props.classes.formControl}
                            error={props.showError.name && !props.applicant.name}
                        >
                            <InputLabel htmlFor="name">
                                Name
                            </InputLabel>
                            <Input
                                id="name"
                                value={props.applicant.name}
                                onChange={(event) => {
                                    props.setApplicantNameField(event);
                                    setStepUncompleted();
                                }}
                                onBlur={() => props.setShowError('name')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                    >
                        <FormControl
                            className={props.classes.formControl}
                            error={props.showError.surname && !props.applicant.surname}
                        >
                            <InputLabel htmlFor="surname">
                                Surname
                            </InputLabel>
                            <Input
                                id="surname"
                                value={props.applicant.surname}
                                onChange={(event) => {
                                    props.setApplicantSurNameField(event);
                                    setStepUncompleted();
                                }}
                                onBlur={() => props.setShowError('surname')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                    >
                        <FormControl
                            className={props.classes.formControl}
                            error={props.showError.dateOfBirth && (!props.applicant.dateOfBirth || !isValidDate(props.applicant.dateOfBirth))}
                        >
                            <InputLabel htmlFor="dateOfBirth">
                                Date of birth
                            </InputLabel>
                            <Input
                                id="dateOfBirth"
                                value={props.applicant.dateOfBirth}
                                onChange={(event) => {
                                    props.setApplicantDateOfBirthField(event);
                                    setStepUncompleted();
                                }}
                                onBlur={() => props.setShowError('dateOfBirth')}
                                inputComponent={BirthDateFormatCustom}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                    >
                        <FormControl
                            className={props.classes.formControl}
                            error={props.showError.addressCity && !props.applicant.address.city}
                        >
                            <InputLabel htmlFor="city">
                                City
                            </InputLabel>
                            <Input
                                id="city"
                                value={props.applicant.address.city}
                                onChange={(event) => {
                                    props.setApplicantCityField(event);
                                    setStepUncompleted();
                                }}
                                onBlur={() => props.setShowError('addressCity')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                    >
                        <FormControl
                            className={props.classes.formControl}
                            error={(
                                props.showError.addressPostalCode
                                && !isValidPostalCode(props.applicant.address.postalCode)
                            )}
                        >
                            <InputLabel htmlFor="postalCode">
                                Postal Code
                            </InputLabel>
                            <Input
                                id="postalCode"
                                value={props.applicant.address.postalCode}
                                onChange={(event) => {
                                    props.setApplicantPostalCodeField(event);
                                    setStepUncompleted();
                                }}
                                onBlur={() => props.setShowError('addressPostalCode')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                    >
                        <FormControl
                            className={props.classes.formControl}
                            error={props.showError.addressStreet && !props.applicant.address.street}
                        >
                            <InputLabel htmlFor="street">
                                Street
                            </InputLabel>
                            <Input
                                id="street"
                                value={props.applicant.address.street}
                                onChange={(event) => {
                                    props.setApplicantStreetField(event);
                                    setStepUncompleted();
                                }}
                                onBlur={() => props.setShowError('addressStreet')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                    >
                        <FormControl
                            className={props.classes.formControl}
                            error={(
                                props.showError.addressHouseNumber
                                && !props.applicant.address.houseNumber
                            )}
                        >
                            <InputLabel htmlFor="houseNumber">
                                House Number
                            </InputLabel>
                            <Input
                                id="houseNumber"
                                value={props.applicant.address.houseNumber}
                                onChange={(event) => {
                                    props.setApplicantHouseNumberField(event);
                                    setStepUncompleted();
                                }}
                                onBlur={() => props.setShowError('addressHouseNumber')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                    >
                        <FormControl
                            className={props.classes.formControl}
                            error={props.showError.phone && !isValidPhone(props.applicant.phone)}
                        >
                            <InputLabel htmlFor="phone">
                                Phone
                            </InputLabel>
                            <Input
                                id="phone"
                                value={props.applicant.phone}
                                onChange={(event) => {
                                    props.setApplicantPhoneField(event);
                                    setStepUncompleted();
                                }}
                                onBlur={() => props.setShowError('phone')}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </div>
            <div className={props.classes.buttons}>
                <Button
                    className={props.classes.button}
                    color="default"
                    onClick={() => props.setStep(2)}
                >
                    Back
                </Button>
                <ContinueButton
                    className={props.classes.button}
                    disabled={!isFormValid()}
                    onClick={() => {
                        goToNextStep();
                    }}
                />
            </div>
        </div>
    );
}

ApplicantDetails.propTypes = {
    setStep: PropTypes.func.isRequired,
    setStepCompleted: PropTypes.func.isRequired,
    applicant: PropTypes.object.isRequired,
    customer: PropTypes.object.isRequired,
    applicationId: PropTypes.string.isRequired,
    setApplicantNameField: PropTypes.func.isRequired,
    setApplicantSurNameField: PropTypes.func.isRequired,
    setApplicantDateOfBirthField: PropTypes.func.isRequired,
    setApplicantCityField: PropTypes.func.isRequired,
    setApplicantStreetField: PropTypes.func.isRequired,
    setApplicantPostalCodeField: PropTypes.func.isRequired,
    setApplicantHouseNumberField: PropTypes.func.isRequired,
    setApplicantPhoneField: PropTypes.func.isRequired,
    setApplicantDetails: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    setShowError: PropTypes.func.isRequired,
    showError: PropTypes.object.isRequired,
};

export default withStyles(styles)(ApplicantDetails);



// WEBPACK FOOTER //
// ./src/app/FormStepper/ApplicantDetails/ApplicantDetails.js